/**
 * The inline CSS properties the editor must carry through untouched.
 *
 * This is the same list as ALLOWED_STYLE_PROPS in
 * src/Clients/ACCB/Components/Notice/noticeHtml.js, which that file describes
 * as "half of a contract with bac/html_docx.py": the backend writes a
 * paragraph's formatting out as inline CSS and reads the same properties back
 * when rebuilding the .docx. A property dropped here is dropped from the
 * printed document - that is how edited notices previously lost their
 * `Sub:`/`-reg.` indents and their page breaks.
 *
 * `text-align` is deliberately absent: the TextAlign extension owns it, and
 * two extensions writing the same declaration would fight.
 */
export const PRESERVED_STYLE_PROPS = [
  "font-weight",
  "font-style",
  "text-decoration",
  "font-family",
  "font-size",
  "color",
  "background-color",
  "margin",
  "margin-left",
  "margin-right",
  "margin-top",
  "margin-bottom",
  "padding",
  "padding-left",
  "padding-right",
  "padding-top",
  "padding-bottom",
  "width",
  "border",
  "border-bottom",
  "border-top",
  "line-height",
  "vertical-align",
  "list-style-type",
  // Paragraph geometry the .docx round trip depends on.
  "text-indent",
  // Both spellings. We write the legacy pair, but a style attribute that
  // passes through the browser's CSSOM comes back normalised to the modern
  // one - that is how saved drafts ended up carrying "break-before: page".
  // Allowing only the legacy names silently dropped it on the next load.
  "page-break-before",
  "page-break-after",
  "break-before",
  "break-after",
];

/** Split a style attribute into [property, value] pairs, lower-casing keys. */
export function parseStyle(value) {
  return String(value || "")
    .split(";")
    .map((declaration) => declaration.trim())
    .filter(Boolean)
    .map((declaration) => {
      const index = declaration.indexOf(":");
      if (index === -1) return null;
      return [
        declaration.slice(0, index).trim().toLowerCase(),
        declaration.slice(index + 1).trim(),
      ];
    })
    .filter(Boolean);
}

/** Rebuild a style attribute from [property, value] pairs. */
export function formatStyle(declarations) {
  return declarations
    .filter(([, value]) => value !== "" && value != null)
    .map(([property, value]) => `${property}: ${value}`)
    .join("; ");
}

/** Drop anything outside the allowlist. Returns "" when nothing survives. */
export function filterStyle(value, allowed = PRESERVED_STYLE_PROPS) {
  return formatStyle(
    parseStyle(value).filter(([property]) => allowed.includes(property))
  );
}
